import React, { useState, useEffect } from 'react';
import './Testimonials.css';
import { db } from '../../firebase';
import { collection, addDoc, getDocs, doc, getDoc, query, orderBy, serverTimestamp } from 'firebase/firestore';

// --- REVIEW ITEM COMPONENT (With Lightbox Logic) ---
const ReviewItem = ({ review }) => {
    const [isExpanded, setIsExpanded] = useState(false);

    // Lightbox State
    const [lightboxOpen, setLightboxOpen] = useState(false);
    const [lightboxImg, setLightboxImg] = useState("");

    // Default image fallback
    const defaultPhoto = 'https://cdn-icons-png.flaticon.com/512/149/149071.png';
    const avatar = review.userPhoto || defaultPhoto;

    // Max text logic
    const MAX_LENGTH = 150;
    const text = review.text || "";
    const isLongText = text.length > MAX_LENGTH;
    const displayText = isExpanded ? text : (isLongText ? text.slice(0, MAX_LENGTH) + "..." : text);

    // Handlers for opening/closing the image
    const openImage = (url) => {
        setLightboxImg(url);
        setLightboxOpen(true);
    };

    const closeImage = (e) => {
        // Close only if clicking the background overlay or close button
        if (e.target.className !== 'lightbox-img-content') {
            setLightboxOpen(false);
            setLightboxImg("");
        }
    };

    return (
        <>
            <div className="review-card">
                <div className="review-header">
                    <div className="reviewer-profile-group">
                        <img src={avatar} alt="User Avatar" className="reviewer-avatar" />
                        <div className="reviewer-info">
                            <strong>{review.userName}</strong>
                            {review.userAge && <span className="reviewer-age"> {review.userAge} </span>}
                        </div>
                    </div>
                    <span className="star-rating">{"⭐".repeat(review.rating)}</span>
                </div>

                <p className="review-text">{displayText}</p>

                {/* --- TRANSFORMATION PHOTOS --- */}
                {review.beforePhoto && review.afterPhoto && (
                    <div className="review-transformation">
                        <div className="transform-photo-box">
                            <span className="transform-label">BEFORE</span>
                            <img
                                src={review.beforePhoto}
                                alt="Before"
                                onClick={() => openImage(review.beforePhoto)}
                                title="Click to enlarge"
                            />
                        </div>
                        <div className="transform-arrow">➜</div>
                        <div className="transform-photo-box">
                            <span className="transform-label">AFTER</span>
                            <img
                                src={review.afterPhoto}
                                alt="After"
                                onClick={() => openImage(review.afterPhoto)}
                                title="Click to enlarge"
                            />
                        </div>
                    </div>
                )}
                {/* ----------------------------- */}

                {isLongText && (
                    <button className="read-more-btn" onClick={() => setIsExpanded(!isExpanded)}>
                        {isExpanded ? "Show Less" : "Show More"}
                    </button>
                )}

                <small className="review-date">
                    {review.createdAt?.toDate().toLocaleDateString()}
                </small>
            </div>

            {/* --- LIGHTBOX MODAL --- */}
            {lightboxOpen && (
                <div className="lightbox-overlay" onClick={closeImage}>
                    <div className="lightbox-content">
                        <button className="lightbox-close" onClick={() => setLightboxOpen(false)}>✕</button>
                        <img src={lightboxImg} alt="Enlarged" className="lightbox-img-content" />
                    </div>
                </div>
            )}
        </>
    );
};

// --- MAIN TESTIMONIALS PAGE ---
function Testimonials({ user }) {
    const [reviews, setReviews] = useState([]);
    const [newReview, setNewReview] = useState("");
    const [rating, setRating] = useState(5);
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        const fetchReviews = async () => {
            try {
                const q = query(collection(db, "testimonials"), orderBy("createdAt", "desc"));
                const querySnapshot = await getDocs(q);
                const reviewsData = querySnapshot.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data()
                }));
                setReviews(reviewsData);
            } catch (err) {
                console.error("Error loading reviews:", err);
            }
        };
        fetchReviews();
    }, []);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError("");
        setLoading(true);

        if (!user) {
            setError("You must be logged in to leave a review.");
            setLoading(false);
            return;
        }

        if (!user.emailVerified) {
            setError("Please verify your email address before posting a review.");
            setLoading(false);
            return;
        }

        if (!newReview.trim()) {
            setError("Review cannot be empty.");
            setLoading(false);
            return;
        }

        try {
            // Get user details (Name, Age, Photo)
            const userDocRef = doc(db, "users", user.uid);
            const userDocSnap = await getDoc(userDocRef);

            let authorName = "Verified User";
            let authorAge = "";
            let authorPhoto = null;

            if (userDocSnap.exists()) {
                const data = userDocSnap.data();
                authorName = data.fullName || "User";
                authorAge = data.age || "";
                authorPhoto = data.photoURL || user.photoURL || null;
            }

            await addDoc(collection(db, "testimonials"), {
                text: newReview,
                rating: Number(rating),
                userName: authorName,
                userAge: authorAge,
                userPhoto: authorPhoto,
                userId: user.uid,
                createdAt: serverTimestamp()
            });

            setNewReview("");
            alert("Review posted!");
            window.location.reload();

        } catch (err) {
            console.error(err);
            setError("Failed to post review. Try again.");
        }
        setLoading(false);
    };

    return (
        <div className="testimonials-container">
            <h1>Community Testimonials</h1>

            <div className="reviews-list">
                {reviews.map((review) => (
                    <ReviewItem key={review.id} review={review} />
                ))}
            </div>

            <div className="review-form-card" style={{ marginTop: '4rem' }}>
                <h3>Leave a Review</h3>
                {user ? (
                    user.emailVerified ? (
                        <form onSubmit={handleSubmit}>
                            <textarea
                                placeholder="Share your experience..."
                                value={newReview}
                                onChange={(e) => setNewReview(e.target.value)}
                                rows="4"
                            />
                            <div className="form-footer">
                                <label>
                                    Rating:
                                    <select value={rating} onChange={(e) => setRating(e.target.value)}>
                                        <option value="5">5 Stars</option>
                                        <option value="4">4 Stars</option>
                                        <option value="3">3 Stars</option>
                                        <option value="2">2 Stars</option>
                                        <option value="1">1 Star</option>
                                    </select>
                                </label>
                                <button type="submit" disabled={loading}>
                                    {loading ? "Posting..." : "Submit Review"}
                                </button>
                            </div>
                            {error && <p className="error-msg">{error}</p>}
                        </form>
                    ) : (
                        <div className="alert-box">
                            <p>⚠️ Your account is created, but not verified.</p>
                            <p>Please check your email to write a review.</p>
                        </div>
                    )
                ) : (
                    <p>Please <a href="/signin">Sign In</a> to leave a review.</p>
                )}
            </div>
        </div>
    );
}

export default Testimonials;